import { PackBox } from "../PackBox";
import { FC, useCallback, useEffect, useState } from "react";
import { dispatch } from "../../dispatch/dispatch";
import { actions } from "../../dispatch";
import getDate from "../../getDate";
import { ageAr, ageLevel, countOrders, countPieces, holdReasonAr, newOrdersMsg, sizeAr, t } from "../../i18n/ar";
import { LocationBadge } from "./LocationBadge";
import { StockChip } from "./StockChip";
import { SkuLink } from "../SkuLink";
import { PrintLabels } from "./PrintLabels";
import { SwapPicker, undoSwap } from "./SwapPicker";
import { PickSort, PickSortBar, comparePick, loadPickSort, savePickSort } from "./PickSort";
import { playNew } from "../../sound";
import StoreScoreChip from "../../../common/StoreScoreChip";
import { IErrorResponse } from "../../interfaces/responses/ErrorResponse.interface";
import {
    HoldReason,
    IPackingOrder,
    IPackingProduct,
    IPackingResponse,
} from "../../interfaces/responses/PackingResponse.interface";

// ─── Packing view (تجهيز الطلبات) ───────────────────────────────────────────
// For the warehouse worker preparing RTS orders. Two tabs:
//   المنتجات — pick list: one card per physical product, big picture + giant
//              REMAINING quantity (orders ticked "تم ✓" are subtracted), so
//              a refresh never loses track of what is still to fetch.
//   الطلبات  — one card per order (oldest first — they must ship first), with
//              a big "تم ✓" button to tick orders off as he packs them.
// Orders that arrived since the previous refresh carry a pulsing 🆕 badge
// (cleared when ticked done) and play a chime, so mid-work refreshes are safe.
// "تم ✓" ticks are stored on the SERVER (PackingDone) so every phone and every
// entry point shows the same state; new/seen sets stay per device in localStorage
// and are pruned to the current RTS list, so they never grow stale.

const SEEN_KEY = "packing.seen";
const NEW_KEY = "packing.new";

const loadSet = (key: string): Set<string> | null => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return null;
};

const saveSet = (key: string, value: Iterable<string>) => {
    try {
        localStorage.setItem(key, JSON.stringify(Array.from(value)));
    } catch {}
};

type Tab = "products" | "orders" | "time";

interface IZoom {
    imageUrl: string;
    name: string;
    qty?: number;
}

interface IPackingProps {
    setShowSpinner: (v: boolean) => void;
}

export const Packing: FC<IPackingProps> = ({ setShowSpinner }) => {
    const [data, setData] = useState<IPackingResponse | null>(null);
    const [error, setError] = useState("");
    const [tab, setTab] = useState<Tab>("products");
    const [done, setDone] = useState<Set<string>>(new Set());
    const [newKeys, setNewKeys] = useState<Set<string>>(() => loadSet(NEW_KEY) || new Set());
    const [zoom, setZoom] = useState<IZoom | null>(null);
    const [now, setNow] = useState<number>(Date.now());
    // Pick list order: time (default) / shelf place / size / quantity — see PickSort.tsx
    const [pickSort, setPickSort] = useState<PickSort>(() => loadPickSort("packing.sort"));
    const changePickSort = (v: PickSort) => { savePickSort("packing.sort", v); setPickSort(v); };

    const load = useCallback(async () => {
        setShowSpinner(true);
        const resp: IPackingResponse | IErrorResponse = await dispatch(actions.getRtsPacking);
        setShowSpinner(false);
        if (!resp || "error" in resp) {
            setError(t.packingLoadError);
            return;
        }
        setError("");
        setData(resp);
        setNow(Date.now());
        const currentKeys = resp.orders.map((o) => o.key);
        const currentSet = new Set(currentKeys);
        // Orders not present at the previous refresh are NEW. The badge sticks
        // (survives page reloads via localStorage) until the order is ticked
        // done or leaves the RTS list. First ever visit flags nothing.
        const prevSeen = loadSet(SEEN_KEY);
        setNewKeys((prev) => {
            const next = new Set(Array.from(prev).filter((k) => currentSet.has(k)));
            let arrivals = 0;
            if (prevSeen) {
                for (const k of currentKeys) {
                    if (!prevSeen.has(k) && !next.has(k)) {
                        next.add(k);
                        arrivals++;
                    }
                }
            }
            if (arrivals > 0) playNew(newOrdersMsg(arrivals));
            saveSet(NEW_KEY, next);
            return next;
        });
        saveSet(SEEN_KEY, currentKeys);
        // "تم ✓" ticks come from the server — shared by every device, survive refreshes
        setDone(new Set(resp.orders.filter((o) => o.done).map((o) => o.key)));
    }, [setShowSpinner]);

    useEffect(() => {
        load();
        // Statuses change as orders ship — refetch every 5 minutes.
        const id = setInterval(load, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [load]);

    const toggleDone = (key: string) => {
        const wasDone = done.has(key);
        setDone((prev) => {
            const next = new Set(prev);
            if (wasDone) next.delete(key);
            else next.add(key);
            return next;
        });
        // Persist on the server; roll back if it fails so the screen never lies.
        const number = data?.orders.find((o) => o.key === key)?.number || "";
        dispatch(actions.setDone, { key, done: !wasDone, number }).then((r: any) => {
            if (!r || "error" in r) {
                setDone((prev) => {
                    const next = new Set(prev);
                    if (wasDone) next.add(key);
                    else next.delete(key);
                    return next;
                });
                setError(t.packingDoneError);
            }
        });
        // Ticking an order done acknowledges it — the 🆕 badge goes away.
        setNewKeys((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            saveSet(NEW_KEY, next);
            return next;
        });
    };

    // "Cannot prepare now" → park the order server-side (all devices see it,
    // the label run skips it, the responsible person gets an email).
    const holdOrder = async (o: IPackingOrder, reason: HoldReason) => {
        const resp = await dispatch(actions.holdOrder, { storeId: o.storeId, orderId: o.orderId, number: o.number, reason });
        if (!resp || "error" in resp) return setError(t.holdError);
        await load();
    };
    const releaseOrder = async (o: IPackingOrder) => {
        const resp = await dispatch(actions.releaseOrder, { storeId: o.storeId, orderId: o.orderId });
        if (!resp || "error" in resp) return setError(t.holdError);
        await load();
    };

    // Held orders live in their own section; every counter below ignores them.
    const heldOrders = data ? data.orders.filter((o) => !!o.hold) : [];
    const activeOrders = data ? data.orders.filter((o) => !o.hold) : [];
    const doneCount = activeOrders.filter((o) => done.has(o.key)).length;
    const remaining = activeOrders.length - doneCount;
    const newCount = activeOrders.filter((o) => newKeys.has(o.key) && !done.has(o.key)).length;
    const oldCount = activeOrders.filter((o) => !done.has(o.key) && o.createdAt && ageLevel(o.createdAt, now) !== "fresh").length;

    // Undone orders first (oldest first — they must ship first), done at the end.
    const sortedOrders = [...activeOrders].sort((a, b) => Number(done.has(a.key)) - Number(done.has(b.key)));

    // Time view: orders bucketed into hourly blocks (Casablanca time), oldest
    // block first — the worker packs top-to-bottom and oldest ships first.
    const timeBlocks = (() => {
        if (!data) return [];
        const map = new Map<string, { key: string; date: string; orders: IPackingOrder[]; pieces: number }>();
        for (const o of activeOrders) {
            const date = o.createdAt || "";
            const key = date ? getDate(new Date(date), "YYYY-MM-DD HH") : "0000";
            let b = map.get(key);
            if (!b) {
                b = { key, date, orders: [], pieces: 0 };
                map.set(key, b);
            }
            b.orders.push(o);
            b.pieces += o.totalPieces;
        }
        const blocks = Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
        for (const b of blocks) b.orders.sort((x, y) => Number(done.has(x.key)) - Number(done.has(y.key)));
        return blocks;
    })();
    // The oldest block that still has unpacked orders — where to start working.
    const startBlockKey = timeBlocks.find((b) => b.orders.some((o) => !done.has(o.key)))?.key;

    // Pick list with live "still to take" math: subtract the qty that belongs
    // to orders already ticked done; find the oldest UNDONE order per product.
    const orderDates = new Map((data?.orders || []).map((o) => [o.key, o.createdAt]));
    const productViews = (data?.products || [])
        .map((p) => {
            const perOrder = p.perOrder || [];
            let taken = 0;
            let oldestUndone: string | null = null;
            let isNew = false;
            for (const po of perOrder) {
                if (done.has(po.key)) {
                    taken += po.qty;
                    continue;
                }
                if (newKeys.has(po.key)) isNew = true;
                const d = orderDates.get(po.key);
                if (d && (!oldestUndone || new Date(d) < new Date(oldestUndone))) oldestUndone = d;
            }
            if (!oldestUndone && p.oldestCreatedAt) oldestUndone = p.oldestCreatedAt;
            return { product: p, taken, left: p.totalQty - taken, oldestUndone, isNew };
        })
        // Still-needed first, then the chosen order; fully-taken sink to the end.
        .sort(comparePick(pickSort));

    return (
        <div className="mt-4">
            {/* Title + refresh */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="flex items-center gap-2 text-3xl font-black text-slate-800">
                    <span aria-hidden>📦</span> {t.packingTitle}
                </h1>
                <button
                    onClick={load}
                    className="flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 py-2 text-xl font-black text-slate-700 shadow-sm transition-colors hover:bg-slate-50 active:bg-slate-100"
                >
                    <span className="text-2xl" aria-hidden>🔄</span> {t.refresh}
                </button>
            </div>

            {error && (
                <p className="mt-4 rounded-2xl bg-red-100 px-4 py-4 text-center text-2xl font-black text-red-700">
                    ⚠️ {error}
                </p>
            )}

            {data && (
                <>
                    {/* Big counters */}
                    <div className={`mt-4 grid gap-2 sm:gap-3 ${["grid-cols-3", "grid-cols-4", "grid-cols-5"][Number(oldCount > 0) + Number(heldOrders.length > 0)]}`}>
                        <StatTile icon="🧾" value={activeOrders.length} label={t.ordersWord} />
                        <StatTile icon="📦" value={data.totalPieces} label={t.piecesWord} />
                        <StatTile icon="🏷️" value={data.totalProducts} label={t.productsWord} />
                        {oldCount > 0 && <StatTile icon="🕐" value={oldCount} label={t.oldTile} danger />}
                        {heldOrders.length > 0 && <StatTile icon="⏸" value={heldOrders.length} label={t.heldTile} warn />}
                    </div>

                    {/* Progress: packed vs remaining vs freshly arrived */}
                    {data.totalOrders > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-lg font-bold">
                            <span className="rounded-xl bg-emerald-100 px-3 py-1.5 text-emerald-700">
                                ✅ {t.packedLabel}: <span className="ltr">{doneCount}</span>
                            </span>
                            <span className="rounded-xl bg-amber-100 px-3 py-1.5 text-amber-700">
                                ⏳ {t.remainingLabel}: <span className="ltr">{remaining}</span>
                            </span>
                            {newCount > 0 && (
                                <span className="animate-pulse rounded-xl bg-sky-500 px-3 py-1.5 text-white shadow">
                                    🆕 {newOrdersMsg(newCount)}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Tabs — big and obvious */}
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        <TabButton active={tab === "products"} onClick={() => setTab("products")} icon="🏷️" label={t.productsTab} />
                        <TabButton active={tab === "orders"} onClick={() => setTab("orders")} icon="🧾" label={t.ordersTab} />
                        <TabButton active={tab === "time"} onClick={() => setTab("time")} icon="⏰" label={t.timeTab} />
                    </div>

                    {data.totalOrders === 0 && (
                        <p className="mt-8 rounded-3xl bg-emerald-50 px-4 py-10 text-center text-3xl font-black text-emerald-700">
                            {t.emptyRts}
                        </p>
                    )}

                    {data.totalOrders > 0 && (
                        <p className="mt-3 text-center text-sm font-bold text-slate-400">{t.tapToZoom} · 📍 {t.locationHint} · {t.stockHint}</p>
                    )}

                    {tab === "products" && data.totalProducts > 0 && <PickSortBar value={pickSort} onChange={changePickSort} />}

                    {tab === "products" && (
                        <section className="mt-3 grid gap-4 md:grid-cols-2">
                            {productViews.map((v) => (
                                <ProductCard
                                    key={v.product.key}
                                    product={v.product}
                                    left={v.left}
                                    taken={v.taken}
                                    isNew={v.isNew}
                                    oldestUndone={v.oldestUndone}
                                    now={now}
                                    onZoom={setZoom}
                                />
                            ))}
                        </section>
                    )}

                    {tab === "orders" && (
                        <section className="mt-3 space-y-4">
                            {sortedOrders.map((o, i) => (
                                <OrderCard
                                    key={o.key}
                                    order={o}
                                    index={i}
                                    isDone={done.has(o.key)}
                                    isNew={newKeys.has(o.key)}
                                    now={now}
                                    onToggle={() => toggleDone(o.key)}
                                    onHold={(reason) => holdOrder(o, reason)}
                                    onRelease={() => releaseOrder(o)}
                                    onZoom={setZoom}
                                    onChanged={load}
                                />
                            ))}
                            <HeldSection orders={heldOrders} now={now} onRelease={releaseOrder} onZoom={setZoom} />
                        </section>
                    )}

                    {tab === "time" && (
                        <section className="mt-3 space-y-8">
                            {(() => {
                                let idx = 0;
                                return timeBlocks.map((b) => {
                                    const allDone = b.orders.every((o) => done.has(o.key));
                                    return (
                                        <div key={b.key}>
                                            <TimeBlockHeader
                                                date={b.date}
                                                now={now}
                                                ordersCount={b.orders.length}
                                                pieces={b.pieces}
                                                isStart={b.key === startBlockKey}
                                                allDone={allDone}
                                            />
                                            <div className="mt-2 space-y-4">
                                                {b.orders.map((o) => (
                                                    <OrderCard
                                                        key={o.key}
                                                        order={o}
                                                        index={idx++}
                                                        isDone={done.has(o.key)}
                                                        isNew={newKeys.has(o.key)}
                                                        now={now}
                                                        onToggle={() => toggleDone(o.key)}
                                                        onHold={(reason) => holdOrder(o, reason)}
                                                        onRelease={() => releaseOrder(o)}
                                                        onZoom={setZoom}
                                                        onChanged={load}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                            <HeldSection orders={heldOrders} now={now} onRelease={releaseOrder} onZoom={setZoom} />
                        </section>
                    )}

                    {/* Last step of the day: everything packed → send labels to print */}
                    {data.totalOrders > 0 && <PrintLabels remainingOrders={remaining} heldOrders={heldOrders.length} />}
                </>
            )}

            {/* Fullscreen zoom — tap anywhere to close */}
            {zoom && (
                <div
                    className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-3"
                    onClick={() => setZoom(null)}
                >
                    <img src={zoom.imageUrl} alt="" className="max-h-[80vh] max-w-[96vw] rounded-2xl bg-white object-contain" />
                    <div className="mt-3 flex items-center gap-4">
                        {zoom.qty != null && (
                            <span className="ltr rounded-2xl bg-amber-400 px-4 py-1 text-5xl font-black text-slate-900">×{zoom.qty}</span>
                        )}
                        <p className="ltr max-w-xl text-center text-xl font-bold text-white">{zoom.name}</p>
                    </div>
                    <button className="mt-4 rounded-2xl bg-white px-8 py-3 text-2xl font-black text-slate-900">✕</button>
                </div>
            )}
        </div>
    );
};

const StatTile: FC<{ icon: string; value: number; label: string; danger?: boolean; warn?: boolean }> = ({ icon, value, label, danger, warn }) => (
    <div
        className={`flex flex-col items-center rounded-2xl border-2 px-2 py-3 shadow-sm ${
            danger ? "animate-pulse border-red-400 bg-red-50" : warn ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
        }`}
    >
        <span className="text-3xl" aria-hidden>{icon}</span>
        <span className={`ltr text-4xl font-black sm:text-5xl ${danger ? "text-red-600" : warn ? "text-amber-700" : "text-slate-900"}`}>{value}</span>
        <span className={`text-base font-bold ${danger ? "text-red-600" : warn ? "text-amber-700" : "text-slate-500"}`}>{label}</span>
    </div>
);

// ── Orders parked "cannot prepare now" — apart, amber, with the reason ──
const HeldSection: FC<{
    orders: IPackingOrder[];
    now: number;
    onRelease: (o: IPackingOrder) => void;
    onZoom: (z: IZoom) => void;
}> = ({ orders, now, onRelease, onZoom }) => {
    if (!orders.length) return null;
    return (
        <div className="mt-8 rounded-3xl border-4 border-amber-300 bg-amber-50 p-3 sm:p-4">
            <h2 className="flex items-center gap-2 text-2xl font-black text-amber-900">
                <span aria-hidden>⏸</span> {t.heldSectionTitle}
            </h2>
            <div className="mt-3 space-y-4">
                {orders.map((o, i) => (
                    <OrderCard
                        key={o.key}
                        order={o}
                        index={i}
                        isDone={false}
                        isNew={false}
                        now={now}
                        onToggle={() => {}}
                        onHold={() => {}}
                        onRelease={() => onRelease(o)}
                        onZoom={onZoom}
                    />
                ))}
            </div>
        </div>
    );
};

const TabButton: FC<{ active: boolean; onClick: () => void; icon: string; label: string }> = ({ active, onClick, icon, label }) => (
    <button
        onClick={onClick}
        className={`flex items-center justify-center gap-2 rounded-2xl border-4 px-4 py-3 text-2xl font-black transition-colors ${
            active
                ? "border-[#f68b1e] bg-[#f68b1e] text-white shadow-lg"
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
        }`}
    >
        <span aria-hidden>{icon}</span> {label}
    </button>
);

// Age chip: how long ago the (oldest undone) order was placed.
// fresh <24h → gray · old 24–48h → amber · critical ≥48h → pulsing red
const AgeChip: FC<{ date: string; now: number; subtle?: boolean }> = ({ date, now, subtle }) => {
    const level = ageLevel(date, now);
    const cls =
        level === "critical"
            ? "animate-pulse bg-red-600 text-white"
            : level === "old"
            ? "bg-amber-400 text-slate-900"
            : subtle
            ? "bg-black/20 text-white"
            : "bg-slate-100 text-slate-600";
    return (
        <span className={`rounded-lg px-2.5 py-1 text-base font-black ${cls}`}>
            🕐 {ageAr(date, now)}
            {level === "critical" && ` — ${t.veryOldBadge}`}
            {level === "old" && ` — ${t.oldBadge}`}
        </span>
    );
};

// ── Hourly block header: day + hour range, counts, age color, start marker ──
// red ≥48h · amber 24–48h · white <24h · green when every order inside is done
const TimeBlockHeader: FC<{
    date: string;
    now: number;
    ordersCount: number;
    pieces: number;
    isStart: boolean;
    allDone: boolean;
}> = ({ date, now, ordersCount, pieces, isStart, allDone }) => {
    const d = date ? new Date(date) : null;
    const dayKey = d ? getDate(d, "YYYY-MM-DD") : "";
    const dayLabel = !d
        ? ""
        : dayKey === getDate(new Date(), "YYYY-MM-DD")
        ? t.todayWord
        : dayKey === getDate(new Date(), "YYYY-MM-DD", 1)
        ? t.yesterdayWord
        : getDate(d, "DD/MM");
    const hour = d ? parseInt(getDate(d, "HH"), 10) : 0;
    const range = `${String(hour).padStart(2, "0")}:00 – ${String((hour + 1) % 24).padStart(2, "0")}:00`;
    const level = d ? ageLevel(d, now) : "fresh";
    const cls = allDone
        ? "border-emerald-500 bg-emerald-100 text-emerald-800"
        : level === "critical"
        ? "border-red-700 bg-red-600 text-white"
        : level === "old"
        ? "border-amber-500 bg-amber-400 text-slate-900"
        : "border-slate-300 bg-white text-slate-800";
    return (
        <div className={`flex flex-wrap items-center gap-3 rounded-2xl border-4 px-4 py-3 shadow ${cls}`}>
            <span className="text-3xl" aria-hidden>{allDone ? "✅" : "🕐"}</span>
            <span className="text-3xl font-black">{dayLabel}</span>
            <span className="ltr rounded-xl bg-black/15 px-3 py-1 font-mono text-2xl font-black tracking-wide">{range}</span>
            {isStart && !allDone && (
                <span className="animate-pulse rounded-xl bg-slate-900 px-3 py-1.5 text-xl font-black text-white">
                    🔥 {t.startHere}
                </span>
            )}
            {d && !allDone && (
                <span className="rounded-xl bg-black/15 px-3 py-1 text-lg font-black">
                    {ageAr(date, now)}
                    {level === "critical" ? ` — ${t.veryOldBadge}` : level === "old" ? ` — ${t.oldBadge}` : ""}
                </span>
            )}
            <span className="ms-auto rounded-xl bg-black/15 px-3 py-1 text-lg font-black">
                {countOrders(ordersCount)} · {countPieces(pieces)}
            </span>
        </div>
    );
};

const NewChip: FC = () => (
    <span className="animate-pulse rounded-lg bg-sky-500 px-2.5 py-1 text-base font-black text-white">🆕 {t.newBadge}</span>
);

// ── Pick list card: picture + giant REMAINING quantity + SKU shelf labels ──
const ProductCard: FC<{
    product: IPackingProduct;
    left: number;
    taken: number;
    isNew: boolean;
    oldestUndone: string | null;
    now: number;
    onZoom: (z: IZoom) => void;
}> = ({ product, left, taken, isNew, oldestUndone, now, onZoom }) => (
    <div
        className={`overflow-hidden rounded-3xl border-2 bg-white shadow-lg ${
            left === 0 ? "border-emerald-300 opacity-60" : isNew ? "border-sky-400" : "border-slate-200"
        }`}
    >
        <div className="flex gap-4 p-4">
            <button
                onClick={() => onZoom({ imageUrl: product.imageUrl, name: product.name, qty: left || product.totalQty })}
                className="shrink-0 self-start"
                title={product.name}
            >
                <img
                    src={product.imageUrl}
                    alt=""
                    className="h-44 w-44 rounded-2xl border border-slate-200 bg-white object-contain sm:h-56 sm:w-56"
                />
            </button>

            <div className="flex min-w-0 flex-1 flex-col gap-3">
                {(isNew || (left > 0 && oldestUndone)) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {isNew && <NewChip />}
                        {left > 0 && oldestUndone && ageLevel(oldestUndone, now) !== "fresh" && (
                            <AgeChip date={oldestUndone} now={now} />
                        )}
                    </div>
                )}

                {/* Giant REMAINING quantity to fetch from the shelf */}
                {left > 0 ? (
                    <div
                        className={`flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2 ${
                            left > 1 ? "bg-amber-100 ring-4 ring-amber-500" : "bg-emerald-50"
                        }`}
                    >
                        <span className="text-xl font-black text-slate-600">{t.take}</span>
                        <span className="ltr text-6xl font-black leading-none text-slate-900">×{left}</span>
                        {taken > 0 && (
                            <span className="ms-auto rounded-lg bg-emerald-100 px-2 py-0.5 text-base font-black text-emerald-700">
                                ✅ <span className="ltr">{taken}</span> {t.takenSoFar}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="rounded-2xl bg-emerald-100 px-4 py-3 text-2xl font-black text-emerald-700">
                        ✅ {t.allTaken}
                    </div>
                )}

                {/* WHERE to walk — giant slot code */}
                <LocationBadge location={product.location} zone={product.zone} overflow={product.overflowLocations} />
                {/* Do we even HAVE it on the shelves? (hint, not the pick qty) */}
                <div><StockChip stock={product.stock} needed={product.totalQty} big /></div>
                {(product.sizeClass === "XL" || product.sizeClass === "XXL" || product.packingNote) && (
                    <div className="flex flex-wrap gap-1.5 text-base font-bold">
                        {(product.sizeClass === "XL" || product.sizeClass === "XXL") && <span className="rounded-lg bg-amber-100 px-2 py-0.5 text-amber-800">📐 {sizeAr(product.sizeClass)}</span>}
                        {product.packingNote && <span className="ltr rounded-lg bg-amber-50 px-2 py-0.5 text-amber-800">📝 {product.packingNote}</span>}
                    </div>
                )}

                {product.pack && <PackBox pack={product.pack} units={product.totalQty} />}
                {!!product.variants?.length && (
                    <div className="flex flex-wrap gap-1.5">
                        {product.variants.map((v) => <span key={v.label} className="ltr inline-flex items-center gap-1.5 rounded-xl border-2 border-amber-400 bg-amber-100 px-3 py-1 text-2xl font-black text-amber-900">📏 {v.label} <span className="rounded-lg bg-amber-900 px-2 text-lg text-white">×{v.qty}</span></span>)}
                    </div>
                )}
                {product.groupName && <p className="ltr inline-block rounded-lg bg-indigo-50 px-2.5 py-1 text-start text-lg font-black uppercase tracking-wide text-indigo-900">📦 {product.groupName}</p>}
                <p className="ltr text-start text-lg font-bold leading-snug text-slate-700">{product.name}</p>

                {/* SKU = the shelf label */}
                <div className="flex flex-wrap gap-1.5">
                    {product.skus.map((sku) => (
                        <SkuLink key={sku} sku={sku} url={product.urls?.[sku]} className="ltr inline-block rounded-lg border-2 border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xl font-black text-slate-900" />
                    ))}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-1.5 text-base font-bold">
                    <span className="rounded-lg bg-sky-100 px-2 py-0.5 text-sky-700">🧾 {countOrders(product.ordersCount)}</span>
                    {product.stores.map((s) => (
                        <span key={s} className="ltr rounded-lg bg-slate-100 px-2 py-0.5 text-slate-600">🏪 {s}</span>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// ── Order card: what goes inside one package ──
const OrderCard: FC<{
    order: IPackingOrder;
    index: number;
    isDone: boolean;
    isNew: boolean;
    now: number;
    onToggle: () => void;
    onHold: (reason: HoldReason) => void;
    onRelease: () => void;
    onZoom: (z: IZoom) => void;
    onChanged?: () => void;
}> = ({ order, index, isDone, isNew, now, onToggle, onHold, onRelease, onZoom, onChanged }) => {
    const multi = order.items.length > 1;
    const held = !!order.hold;
    const [pickReason, setPickReason] = useState(false);
    const [swapFor, setSwapFor] = useState<IPackingOrder["items"][number] | null>(null);   // 🔁 picking the product sent instead
    const reasons: Array<{ key: HoldReason; icon: string; label: string }> = [
        { key: "no_stock", icon: "🏬🚫", label: t.reasonNoStock },
        { key: "damaged", icon: "📦💥", label: t.reasonDamaged },
        { key: "not_found", icon: "🔍❓", label: t.reasonNotFound },
        { key: "other", icon: "❔", label: t.reasonOther },
    ];
    return (
        <div
            className={`overflow-hidden rounded-3xl border-4 bg-white shadow-lg transition-opacity ${
                held ? "border-amber-500" : isDone ? "border-emerald-500 opacity-60" : multi ? "border-amber-400" : "border-slate-200"
            }`}
        >
            {/* Order header: number + store + age + done button */}
            <div className={`flex flex-wrap items-center gap-3 px-4 py-3 ${held ? "bg-amber-600" : isDone ? "bg-emerald-600" : "bg-slate-800"} text-white`}>
                <span className="ltr flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-xl font-black text-slate-900">
                    {index + 1}
                </span>
                <div className="min-w-0">
                    <p className="ltr text-start font-mono text-2xl font-black tracking-wider sm:text-3xl">{order.number}</p>
                    <p className="text-sm font-bold text-white/80">
                        🏪{" "}
                        <span className="ltr">
                            {order.storeName}
                            {order.createdAt ? ` · ${getDate(new Date(order.createdAt), "DD/MM HH:mm")}` : ""}
                        </span>
                        <StoreScoreChip store={order.storeName} />
                    </p>
                </div>
                {!isDone && !held && isNew && <NewChip />}
                {!isDone && order.createdAt && <AgeChip date={order.createdAt} now={now} subtle />}
                <span className="ms-auto rounded-lg bg-black/20 px-3 py-1 text-lg font-black">{countPieces(order.totalPieces)}</span>
                {held ? (
                    <button
                        onClick={onRelease}
                        className="rounded-2xl bg-white px-5 py-2.5 text-xl font-black text-emerald-700 shadow transition-colors hover:bg-emerald-50"
                    >
                        ▶️ {t.releaseBtn}
                    </button>
                ) : (
                    <>
                        {!isDone && (
                            <button
                                onClick={() => setPickReason((v) => !v)}
                                title={t.holdBtn}
                                className={`rounded-2xl border-2 px-3 py-2 text-base font-black transition-colors ${
                                    pickReason ? "border-amber-300 bg-amber-400 text-slate-900" : "border-white/30 bg-black/20 text-white/90 hover:bg-black/30"
                                }`}
                            >
                                ⏸ {t.holdBtn}
                            </button>
                        )}
                        <button
                            onClick={onToggle}
                            className={`rounded-2xl px-5 py-2.5 text-2xl font-black shadow transition-colors ${
                                isDone ? "bg-white text-emerald-700" : "bg-emerald-500 text-white hover:bg-emerald-400"
                            }`}
                        >
                            {isDone ? t.undoBtn : t.doneBtn}
                        </button>
                    </>
                )}
            </div>

            {/* Why it waits (held) */}
            {held && order.hold && (
                <div className="flex flex-wrap items-center gap-2 bg-amber-100 px-4 py-3 text-xl font-black text-amber-900">
                    <span>⏸ {t.heldBadge} — {holdReasonAr(order.hold.reason)}</span>
                    {order.hold.since && <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">🕐 {ageAr(order.hold.since, now)}</span>}
                    <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">📧 {t.heldNotified}</span>
                </div>
            )}

            {/* Reason picker for "cannot prepare now" */}
            {!held && pickReason && (
                <div className="border-b-4 border-amber-400 bg-amber-50 p-3">
                    <p className="text-xl font-black text-amber-900">⏸ {t.holdTitle}</p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {reasons.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => { setPickReason(false); onHold(r.key); }}
                                className="rounded-2xl border-2 border-amber-300 bg-white px-3 py-4 text-xl font-black text-slate-800 shadow-sm hover:bg-amber-100"
                            >
                                <span className="me-2" aria-hidden>{r.icon}</span>{r.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setPickReason(false)}
                        className="mt-2 w-full rounded-xl px-3 py-2 text-lg font-bold text-slate-500 hover:bg-amber-100"
                    >
                        ↩️ {t.holdCancel}
                    </button>
                </div>
            )}

            {/* Items */}
            <div className="divide-y divide-slate-100">
                {order.items.map((it) => (
                    <div key={it.sku} className="flex items-center gap-4 p-3 sm:p-4">
                        <button onClick={() => onZoom({ imageUrl: it.imageUrl, name: it.name, qty: it.qty })} className="shrink-0" title={it.name}>
                            <img
                                src={it.imageUrl}
                                alt=""
                                className="h-28 w-28 rounded-xl border border-slate-200 bg-white object-contain sm:h-36 sm:w-36"
                            />
                        </button>
                        <div className="min-w-0 flex-1">
                            <span
                                className={`ltr inline-block rounded-xl px-3 py-1 text-4xl font-black text-slate-900 ${
                                    it.qty > 1 ? "bg-amber-200 ring-2 ring-amber-500" : "bg-slate-100"
                                }`}
                            >
                                ×{it.qty}
                            </span>
                            {it.location ? (
                                <span className="ms-2 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-1 text-2xl font-black text-white">📍 <span className="text-base text-amber-300">{t.locationWord}</span> <span className="ltr font-mono tracking-widest">{it.location}</span></span>
                            ) : (
                                <span className="ms-2 inline-block rounded-xl bg-red-100 px-2 py-1 text-base font-black text-red-700">📍 {t.noLocation}</span>
                            )}
                            {it.pack && <div className="mt-1.5"><PackBox pack={it.pack} units={it.qty} compact /></div>}
                            {it.variation && <p className="ltr mt-1.5 inline-flex items-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-100 px-3 py-1 text-xl font-black text-amber-900">📏 <span className="text-sm font-bold text-amber-700">المقاس / اللون</span> {it.variation}</p>}
                            {it.groupName && <p className="ltr mt-1.5 inline-block rounded-lg bg-indigo-50 px-2 py-0.5 text-start text-base font-black uppercase tracking-wide text-indigo-900">📦 {it.groupName}</p>}
                            <p className="ltr mt-1.5 text-start text-base font-bold leading-snug text-slate-700">{it.name}</p>
                            <p className="ltr text-start font-mono text-lg font-black text-slate-500"><SkuLink sku={it.sku} url={it.url} /></p>
                            {it.stock != null && it.stock <= 0 && (
                                <div className="mt-1"><StockChip stock={it.stock} /></div>
                            )}
                            {/* 🔁 sent another product for this line (not found on the shelf) */}
                            {it.swap ? (
                                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-2xl border-2 border-violet-400 bg-violet-50 p-2">
                                    {it.swap.image ? <img src={it.swap.image} alt="" className="h-14 w-14 rounded-lg bg-white object-contain" /> : null}
                                    <div className="min-w-0 flex-1"><p className="text-base font-black text-violet-900">🔁 صيفطتي بلاصتو:</p><p className="ltr truncate text-start text-lg font-black text-slate-900">{it.swap.name}</p></div>
                                    {!isDone && <button onClick={async () => { await undoSwap({ storeId: order.storeId, orderId: order.orderId, sku: it.sku }); onChanged?.(); }} className="rounded-xl bg-white px-3 py-2 text-base font-black text-slate-600 ring-1 ring-slate-300">↩️ إلغاء</button>}
                                </div>
                            ) : !isDone && !held && (
                                <button onClick={() => setSwapFor(it)} className="mt-2 rounded-2xl border-2 border-violet-300 bg-white px-3 py-2 text-lg font-black text-violet-800 hover:bg-violet-50">🔁 ما لقيتوش — صيفطت منتج آخر</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {swapFor && (
                <SwapPicker storeId={order.storeId} orderId={order.orderId} orderNumber={order.number} sku={swapFor.sku} qty={swapFor.qty}
                    orderedName={swapFor.groupName || swapFor.name} orderedImage={swapFor.imageUrl}
                    onClose={() => setSwapFor(null)} onDone={() => { setSwapFor(null); onChanged?.(); }} />
            )}
        </div>
    );
};
